// src/services/productServices.js
import api from './api';

/* ---------------------------------------
   ERROR HANDLER
--------------------------------------- */
const handleError = (error, context = 'Unknown error') => {
  console.error(
    `❌ ${context}:`,
    error.response?.data || error.message || error
  );
  throw error;
};

const getFirstSuccessful = async (paths = []) => {
  let lastError;
  for (const path of paths) {
    try {
      const res = await api.get(path);
      return res.data;
    } catch (error) {
      lastError = error;
      if (error?.response?.status !== 404) throw error;
    }
  }
  throw lastError;
};

const postFirstSuccessful = async (paths = [], data, config = undefined) => {
  let lastError;
  for (const path of paths) {
    try {
      const res = await api.post(path, data, config);
      return res.data;
    } catch (error) {
      lastError = error;
      if (error?.response?.status !== 404) throw error;
    }
  }
  throw lastError;
};

const putFirstSuccessful = async (paths = [], data, config = undefined) => {
  let lastError;
  for (const path of paths) {
    try {
      const res = await api.put(path, data, config);
      return res.data;
    } catch (error) {
      lastError = error;
      if (error?.response?.status !== 404) throw error;
    }
  }
  throw lastError;
};

/* =======================
   PRODUCTS
======================= */

export const fetchAllProducts = async () => {
  try {
    const res = await api.get('/products');
    return res.data;
  } catch (error) {
    handleError(error, 'Failed to fetch all products');
  }
};

export const searchProducts = async (query) => {
  try {
    const res = await api.get('/products', {
      params: { search: query },
    });
    return res.data;
  } catch (error) {
    console.error('❌ Failed to search products:', error);
    return [];
  }
};


/* =======================
   BULK IMPORT
======================= */

export const bulkImportProducts = async (file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const res = await api.post(
      '/products/bulk-import',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );

    return res.data; // { total, success, failed, errors }
  } catch (error) {
    handleError(error, 'Failed to bulk import products');
  }
};



export const fetchProductById = async (id) => {
  try {
    const res = await api.get(`/products/${id}`);
    return res.data;
  } catch (error) {
    handleError(error, `Failed to fetch product ${id}`);
  }
};

export const createProduct = async (productData) => {
  try {
    const res = await api.post('/products', productData);
    return res.data;
  } catch (error) {
    handleError(error, 'Failed to create product');
  }
};

export const updateProduct = async (id, productData) => {
  try {
    const res = await api.put(`/products/${id}`, productData);

    // Safety sync: older product update handlers may save core fields but skip
    // the many-to-many relation tables. This makes edit mode reliable without
    // affecting normal product updates.
    const hasRelationPayload = productData && (
      Object.prototype.hasOwnProperty.call(productData, 'additional_category_ids') ||
      Object.prototype.hasOwnProperty.call(productData, 'occasion_ids') ||
      Object.prototype.hasOwnProperty.call(productData, 'tag_ids')
    );

    if (hasRelationPayload) {
      await putFirstSuccessful([
        `/products/${id}/relations`,
        `/fb/products/${id}/relations`
      ], {
        category_id: productData.category_id,
        additional_category_ids: productData.additional_category_ids || [],
        occasion_ids: productData.occasion_ids || [],
        tag_ids: productData.tag_ids || []
      });
    }

    return res.data;
  } catch (error) {
    handleError(error, `Failed to update product ${id}`);
  }
};

export const deleteProduct = async (id) => {
  try {
    const res = await api.delete(`/products/${id}`);
    return res.data;
  } catch (error) {
    console.error(`❌ Failed to delete product ${id}:`, error);
    throw error;
  }
};

/* =======================
   PRODUCT IMAGE
======================= */

export const uploadProductImage = async (file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const res = await api.post('/upload/product-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    return res.data; // { url }
  } catch (error) {
    handleError(error, 'Failed to upload product image');
  }
};

/* =======================
   ATTRIBUTES
======================= */

export const createAttribute = async (data) => {
  try {
    const res = await api.post('/attributes', data);
    return res.data;
  } catch (error) {
    console.error('❌ Failed to create attribute:', error);
    throw error;
  }
};

export const getAllAttributes = async () => {
  try {
    const res = await api.get('/attributes');
    return res.data;
  } catch (error) {
    console.error('❌ Failed to fetch attributes:', error);
    throw error;
  }
};

export const updateAttribute = async (id, data) => {
  try {
    const res = await api.put(`/attributes/${id}`, data);
    return res.data;
  } catch (error) {
    console.error(`❌ Failed to update attribute ${id}:`, error);
    throw error;
  }
};

export const deleteAttribute = async (id) => {
  try {
    const res = await api.delete(`/attributes/${id}`);
    return res.data;
  } catch (error) {
    console.error(`❌ Failed to delete attribute ${id}:`, error);
    throw error;
  }
};

/* =======================
   ATTRIBUTE OPTIONS
======================= */

export const createAttributeOption = async ({ attribute_id, value }) => {
  try {
    const res = await api.post(
      `/attributes/${attribute_id}/options`,
      { value }
    );
    return res.data;
  } catch (error) {
    console.error('❌ Failed to create attribute option:', error);
    throw error;
  }
};

export const getAttributeOptions = async (attribute_id) => {
  try {
    const res = await api.get(
      `/attributes/${attribute_id}/options`
    );
    return res.data;
  } catch (error) {
    console.error('❌ Failed to fetch attribute options:', error);
    throw error;
  }
};

export const deleteAttributeOption = async (optionId) => {
  try {
    const res = await api.delete(`/attribute-options/${optionId}`);
    return res.data;
  } catch (error) {
    console.error(`❌ Failed to delete attribute option ${optionId}:`, error);
    throw error;
  }
};

/* =======================
   VARIANTS
======================= */

export const createVariant = async (variantData) => {
  try {
    const res = await api.post('/variants', variantData);
    return res.data;
  } catch (error) {
    handleError(error, 'Failed to create variant');
  }
};

export const getVariantsByProduct = async (productId) => {
  try {
    const res = await api.get(`/products/${productId}/variants`);
    return res.data;
  } catch (error) {
    handleError(error, `Failed to get variants for product ${productId}`);
  }
};

/* =======================
   PACKAGING
======================= */

export const getProductPackaging = async (productId) => {
  try {
    const res = await api.get(`/products/${productId}/packaging`);
    return res.data;
  } catch (error) {
    handleError(error, `Failed to fetch packaging for product ${productId}`);
  }
};

export const addProductPackaging = async (productId, packagingData) => {
  try {
    const res = await api.post(
      `/products/${productId}/packaging`,
      packagingData
    );
    return res.data;
  } catch (error) {
    handleError(error, 'Failed to add product packaging');
  }
};

export const addVariantPackaging = async (variantId, packagingData) => {
  try {
    const res = await api.post(
      `/variants/${variantId}/packaging`,
      packagingData
    );
    return res.data;
  } catch (error) {
    handleError(error, 'Failed to add variant packaging');
  }
};

/* =======================
   CATEGORIES
======================= */

export const getCategories = async () => {
  try {
    const res = await api.get('/categories');
    return res.data;
  } catch (error) {
    handleError(error, 'Failed to get categories');
  }
};

export const getCategoryById = async (id) => {
  try {
    const res = await api.get(`/categories/${id}`);
    return res.data;
  } catch (error) {
    handleError(error, `Failed to get category ${id}`);
  }
};

export const createCategory = async (data) => {
  try {
    const res = await api.post('/categories', data);
    return res.data;
  } catch (error) {
    console.error('❌ Failed to create category:', error);
    throw error;
  }
};

export const updateCategory = async (id, data) => {
  try {
    const res = await api.put(`/categories/${id}`, data);
    return res.data;
  } catch (error) {
    console.error(`❌ Failed to update category ${id}:`, error);
    throw error;
  }
};

export const deleteCategory = async (id) => {
  try {
    const res = await api.delete(`/categories/${id}`);
    return res.data;
  } catch (error) {
    console.error(`❌ Failed to delete category ${id}:`, error);
    throw error;
  }
};

export const reorderCategories = async (orderArray) => {
  try {
    const res = await api.post('/categories/order', { order: orderArray });
    return res.data;
  } catch (error) {
    console.error('❌ Failed to reorder categories:', error);
    throw error;
  }
};

/* =======================
   INGREDIENTS
======================= */

export const getIngredients = async () => {
  try {
    const res = await api.get('/ingredients');
    return res.data;
  } catch (error) {
    console.error('❌ Failed to fetch ingredients:', error);
    return [];
  }
};

export const getProductIngredients = async (productId) => {
  try {
    const res = await api.get(`/products/${productId}/ingredients`);
    return res.data;
  } catch (error) {
    console.error(`❌ Failed to fetch product ingredients ${productId}:`, error);
    return [];
  }
};

/* =======================
   PRODUCT IMAGES
======================= */

export const getProductImages = async (productId) => {
  try {
    const res = await api.get(`/products/${productId}/images`);
    return res.data;
  } catch (error) {
    console.error(`❌ Failed to fetch product images ${productId}:`, error);
    return [];
  }
};

export const addProductImage = async (productId, mediaOrUrl, altText = '', displayOrder = 0, isPrimary = false) => {
  try {
    const payload = typeof mediaOrUrl === 'object' && mediaOrUrl !== null
      ? mediaOrUrl
      : { image_url: mediaOrUrl, media_type: 'image', alt_text: altText, display_order: displayOrder, is_primary: isPrimary };
    const res = await api.post(`/products/${productId}/images`, payload);
    return res.data;
  } catch (error) {
    handleError(error, 'Failed to add product media');
  }
};

export const getProductReviews = async (productId) => {
  try {
    const res = await api.get(`/products/${productId}/reviews`);
    return res.data;
  } catch (error) {
    console.error(`❌ Failed to fetch product reviews ${productId}:`, error);
    return [];
  }
};

export const deleteProductImage = async (productId, imageId) => {
  try {
    const res = await api.delete(`/products/${productId}/images/${imageId}`);
    return res.data;
  } catch (error) {
    console.error(`❌ Failed to delete product image:`, error);
    throw error;
  }
};

/* =======================
   BUNDLE ITEMS
======================= */

export const getProductBundleItems = async (productId) => {
  try {
    const res = await api.get(`/products/${productId}/bundle-items`);
    return res.data;
  } catch (error) {
    console.error(`❌ Failed to fetch bundle items ${productId}:`, error);
    return [];
  }
};

export const updateProductBundleItems = async (productId, bundleItems) => {
  try {
    const res = await api.put(`/products/${productId}/bundle-items`, {
      bundle_items: bundleItems
    });
    return res.data;
  } catch (error) {
    handleError(error, 'Failed to update bundle items');
  }
};

/* =======================
   PRODUCT INGREDIENTS RELATION
======================= */

export const updateProductIngredients = async (productId, ingredientIds) => {
  try {
    const res = await api.put(`/products/${productId}/ingredients`, {
      ingredient_ids: ingredientIds
    });
    return res.data;
  } catch (error) {
    handleError(error, 'Failed to update product ingredients');
  }
};

/* =======================
   FUSION BOX PRODUCT RELATIONS
======================= */

export const getProductOccasions = async () => {
  try {
    const res = await api.get('/fb/occasions');
    return res.data;
  } catch (error) {
    console.error('❌ Failed to fetch product occasions:', error);
    return [];
  }
};


export const createProductOccasion = async (formData) => {
  try {
    const res = await api.post('/fb/occasions', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return res.data;
  } catch (error) {
    handleError(error, 'Failed to create product occasion');
  }
};

export const getProductTags = async () => {
  try {
    const data = await getFirstSuccessful(['/fb/product-tags', '/product-tags']);
    return Array.isArray(data) ? data : [];
  } catch (error) {
    // Tags are optional badges. Do not break product add/edit if an older
    // backend has not been migrated yet.
    console.warn('Product tags endpoint unavailable; continuing without tags.', error?.response?.status || error?.message);
    return [];
  }
};

export const createProductTag = async (data) => {
  try {
    return await postFirstSuccessful(['/fb/product-tags', '/product-tags'], data);
  } catch (error) {
    handleError(error, 'Failed to create product tag');
  }
};
